import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL } from '@warp-drive/utilities';

// =====================================================================
// Helpers
// =====================================================================

/**
 * Creates a base RequestInfo object for a given operation.
 * @param op - The operation type (e.g., 'findAll', 'findRecord').
 * @param id - The record ID (optional).
 * @returns A base RequestInfo object.
 */
function createBaseRequest(op: 'findAll' | 'createRecord'): RequestInfo;
function createBaseRequest(
  op: 'findRecord' | 'updateRecord' | 'deleteRecord',
  id: string
): RequestInfo;
function createBaseRequest(op: RequestInfo['op'], id?: string): RequestInfo {
  const identifier: { type: string; id?: string } = { type: 'report' };
  if (id) {
    identifier.id = id;
  }

  const url = buildBaseURL({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    identifier: identifier as any,
    op: op as 'findRecord',
    resourcePath: 'api/v2/reports',
  });
  return { url, method: 'GET' }; // Default method
}

/**
 * Converts an object with camelCase keys to a new object with dasherized keys.
 * Undefined values are omitted from the result.
 * @param obj - The object to convert.
 * @returns A new object with dasherized keys.
 */
function toDasherized(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        const dasherizedKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        acc[dasherizedKey] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
}

/**
 * Builds a query string from a parameters object, converting keys to snake_case.
 * @param params - The parameters for the query string.
 * @returns A URL-encoded query string.
 */
function buildQueryString(params: Record<string, string | undefined>): string {
  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      queryParams.append(`filter[${snakeCaseKey}]`, value);
    }
  }
  return queryParams.toString();
}

// =====================================================================
// Public Functions
// =====================================================================

export function queryReportsRequest(params?: {
  reportType?: string;
  visibilityLevel?: string;
  name?: string;
}): RequestInfo {
  const request = createBaseRequest('findAll');
  const queryString = buildQueryString(params || {});

  if (queryString) {
    request.url = `${request.url}?${queryString}`;
  }

  return request;
}

export function getReportRequest(id: string): RequestInfo {
  return createBaseRequest('findRecord', id);
}

export function createReportRequest(data: {
  name: string;
  description?: string;
  reportType: string;
  searchConfig: Record<string, unknown>;
  visibilityLevel?: string;
  partnerAccessConfig?: number[];
  metadata?: Record<string, unknown>;
}): RequestInfo {
  const request = createBaseRequest('createRecord');
  request.method = 'POST';
  request.body = JSON.stringify({
    data: {
      type: 'reports',
      attributes: toDasherized(data),
    },
  });
  return request;
}

export function updateReportRequest(
  id: string,
  data: {
    name?: string;
    description?: string;
    reportType?: string;
    searchConfig?: Record<string, unknown>;
    visibilityLevel?: string;
    partnerAccessConfig?: number[];
    metadata?: Record<string, unknown>;
  }
): RequestInfo {
  const request = createBaseRequest('updateRecord', id);
  request.method = 'PATCH';
  request.body = JSON.stringify({
    data: {
      type: 'reports',
      id,
      attributes: toDasherized(data),
    },
  });
  return request;
}

export function deleteReportRequest(id: string): RequestInfo {
  const request = createBaseRequest('deleteRecord', id);
  request.method = 'DELETE';
  return request;
}
